import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const GlowPortalLink = ({ children, to }) => (
  <Link to={to} className="relative inline-block group cursor-pointer">
    <motion.span
      className="relative z-10 font-serif italic text-gold"
      style={{ textShadow: '0 0 8px rgba(212,175,55,0.5)' }}
      whileHover={{ textShadow: '0 0 15px rgba(212,175,55,1), 0 0 40px rgba(212,175,55,0.6)' }}
      animate={{ opacity: [0.8, 1, 0.8] }}
      transition={{ duration: 3, repeat: Infinity }}>
      {children}
    </motion.span>
    <span className="absolute bottom-0 left-0 h-[1px] w-0 group-hover:w-full transition-all duration-700"
      style={{ background: 'linear-gradient(to right, #D4AF37, #FCF6BA, #D4AF37)' }} />
  </Link>
);

const Foundation = () => (
  <section className="relative py-28 bg-dark border-t border-white/5 overflow-hidden">
    {/* Floating particles */}
    {[...Array(8)].map((_, i) => (
      <motion.div key={i}
        className="absolute rounded-full bg-gold pointer-events-none"
        style={{
          width: Math.random() * 4 + 1,
          height: Math.random() * 4 + 1,
          left: `${10 + Math.random() * 80}%`,
          top: `${10 + Math.random() * 80}%`,
          opacity: 0.2,
        }}
        animate={{ y: [-30, 30, -30], opacity: [0.05, 0.35, 0.05] }}
        transition={{ duration: 5 + Math.random() * 5, repeat: Infinity, delay: Math.random() * 4 }}
      />
    ))}
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_60%,rgba(212,175,55,0.05)_0%,transparent_60%)]" />

    <div className="relative z-10 max-w-4xl mx-auto px-6">
      <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
        className="text-gold tracking-[0.35em] uppercase text-xs mb-10 text-center font-semibold">
        Das Fundament
      </motion.p>

      <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }} transition={{ duration: 0.9 }}
        className="text-4xl md:text-5xl font-serif text-cream text-center leading-tight mb-14">
        Du bist hier, weil du tief in dir spürst,{' '}
        <span className="italic text-transparent bg-clip-text bg-gold-gradient">
          dass sich etwas verändern muss.
        </span>
      </motion.h2>

      <div className="space-y-8 text-lg text-cream/65 font-light leading-relaxed">
        {[
          {
            delay: 0.2,
            text: 'Es geht nicht darum, den anderen zu verändern, sondern dich selbst neu zu erkennen.',
          },
          {
            delay: 0.4,
            rich: true,
            content: (
              <>
                Auf diesem Weg löst du alte emotionale Muster, verstehst deine unbewussten
                Beziehungsdynamiken und lernst endlich, dich selbst an die erste Stelle zu setzen.
                Du verbindest tiefe innere Heilung mit klarer Mindset-Arbeit und erschaffst dir so
                ein völlig neues Fundament:{' '}
                <GlowPortalLink to="/beziehungsheilung-fuer-frauen">Selbstwert</GlowPortalLink>,{' '}
                <GlowPortalLink to="/beziehungsheilung-fuer-frauen">innere Ruhe</GlowPortalLink>{' '}
                und bewusste Entscheidungen in der Liebe.
              </>
            ),
          },
          {
            delay: 0.6,
            text: 'Ich begleite dich Schritt für Schritt durch diesen Prozess. Damit du aus der Unsicherheit, dem Warten und der Abhängigkeit aussteigst und eine Beziehung auf Augenhöhe leben kannst. Eine Beziehung, in der du dich gewählt fühlst – weil du dich selbst gewählt hast.',
          },
        ].map((block, i) => (
          <motion.p key={i}
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ delay: block.delay, duration: 0.8 }}
            className="text-lg text-cream/65 font-light leading-relaxed">
            {block.rich ? block.content : block.text}
          </motion.p>
        ))}
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }} transition={{ delay: 0.9 }}
        className="mt-16 flex flex-col sm:flex-row gap-6 justify-center items-center">
        <Link to="/shop">
          <motion.div
            whileHover={{ scale: 1.06, boxShadow: '0 0 60px rgba(212,175,55,0.7), 0 0 120px rgba(212,175,55,0.3)' }}
            whileTap={{ scale: 0.97 }}
            className="px-12 py-5 bg-gold-gradient text-dark font-bold uppercase tracking-widest text-sm cursor-pointer relative overflow-hidden group"
            style={{ boxShadow: '0 0 30px rgba(212,175,55,0.4)', borderRadius: '2px' }}>
            <span className="relative z-10">✦ Zum Love Code Onlinekurs</span>
            <div className="absolute inset-0 bg-white/10 translate-x-[-110%] skew-x-[-20deg] group-hover:translate-x-[200%] transition-transform duration-1000 ease-in-out" />
          </motion.div>
        </Link>
        <Link to="/contact">
          <motion.div
            whileHover={{ scale: 1.04, boxShadow: '0 0 30px rgba(212,175,55,0.3)', borderColor: 'rgba(212,175,55,0.8)' }}
            whileTap={{ scale: 0.97 }}
            className="px-12 py-5 border border-gold/40 text-gold font-sans tracking-widest uppercase text-sm cursor-pointer transition-all duration-500"
            style={{ borderRadius: '2px' }}>
            Kostenfreies Erstgespräch
          </motion.div>
        </Link>
      </motion.div>
    </div>
  </section>
);

export default Foundation;